// ConversationsRail — the left rail (spec §3 / §5).
//
// Three sections, ALWAYS in that order: channels → DMs → threads. Channels and
// DMs are PLACES (persistent, scrollable); threads are a QUEUE (open, assignable,
// resolvable). Putting them in one list would sit a 12,989-message DM next to a
// three-message email as if they were the same kind of thing — they are not.
//
// Quiet channels are hidden (90 days silent) unless "Show quiet" is on, so ninety
// channels never become ninety rows of nothing. Task comments are deliberately
// excluded — the rail offers no task scope (25,391 threads, zero open; they
// render on the task, not here).
//
// The threads section has three sub-lenses: Open (all open), Awaiting us
// (awaiting_us=true — the only thing that gets an unread badge), Design
// (design_review channel). Paginates with next_before (969 open → 100, then more).
//
// This component is presentational — the page owns the fetch state and passes
// data + callbacks. It never touches supabase (00_CONTRACT.md §11).

import React from "react";
import { EmptyState, ErrorState } from "../../shared/StateMessage";
import PanelErrorBoundary from "../../shared/PanelErrorBoundary";
import { fmtNumber } from "../../shared/format";
import ChannelRow from "./ChannelRow";
import DmRow from "./DmRow";
import ThreadRow from "./ThreadRow";
import {
  ChannelsRowsSkeleton,
  DmsRowsSkeleton,
  ThreadsRowsSkeleton,
} from "./ConversationsSkeleton";

// The threads sub-lenses. Each maps to a threads query (the page applies it):
//   open    → { scope: 'open' }
//   awaiting→ { scope: 'open' } filtered to awaiting_us === true
//   design  → { scope: 'open', channel: 'design_review' }
const THREAD_LENS = [
  { key: "open", label: "Open" },
  { key: "awaiting", label: "Awaiting us" },
  { key: "design", label: "Design" },
];

// A section header: label + count + optional trailing control. The count is a
// plain label here, not a <DrillSheet> trigger — the fn_drill `what` mapping for
// these counts is UNCONFIRMED (see epics C1). A broken drill is worse than a label.
const SectionHeader = ({ label, count, trailing }) => (
  <div className="flex items-center gap-2 px-3 pb-1 pt-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </p>
    {typeof count === "number" && (
      <span className="text-xs tabular-nums text-gray-400">{fmtNumber(count)}</span>
    )}
    {trailing && <div className="ml-auto">{trailing}</div>}
  </div>
);

/**
 * @param {object} props
 * @param {object} props.rooms            { channels[], dms[], counts{channels,dms,open_threads} }
 * @param {number} [props.openThreadsCount]  Polled open-threads count (fresh, in step with the sidebar badge); falls back to rooms.counts.open_threads.
 * @param {object} props.threads          { items[], counts{open,mine,unassigned,total}, next_before }
 * @param {{open?:number|null, awaiting?:number|null, design?:number|null}} [props.threadLensCounts]
 *   Per-lens totals shown next to each tab. `open` is the polled open-threads
 *   total; `design` is the design_review-scoped total; `awaiting` is derived
 *   from the loaded open items (no server scope exists for awaiting_us yet —
 *   see ConversationsPage). A null/undefined entry renders no count for that tab.
 * @param {'open'|'awaiting'|'design'} props.threadLens
 * @param {(lens: string) => void} props.onThreadLensChange
 * @param {boolean} props.showQuiet
 * @param {() => void} props.onToggleQuiet
 * @param {() => void} [props.onLoadMoreThreads]  Called when next_before is present.
 * @param {{type: string, id: string}|null} props.selection
 * @param {(type: string, id: string) => void} props.onSelect
 * @param {'loading'|'ready'|'error'} [props.channelsStatus='ready']  independent channel loader
 * @param {'loading'|'ready'|'error'} [props.dmsStatus='ready']       independent DM loader
 * @param {'loading'|'ready'|'error'} [props.threadsStatus='ready']   independent threads loader
 * @param {() => void} [props.onRetryRooms]    retry channels+DMs (one rooms RPC)
 * @param {() => void} [props.onRetryThreads]  retry threads
 *
 * Each section renders its own skeleton / error / loaded state independently,
 * so the rail fills in progressively (channels → DMs → threads) instead of
 * waiting on the slowest fetch. Defaults to 'ready' so callers that hand over
 * already-loaded data (and the presentational tests) see the data, not loaders.
 */
export default function ConversationsRail({
  rooms,
  // Polled open-threads count (fresh, in step with the sidebar badge). Falls
  // back to rooms.counts.open_threads until the first poll lands / on error.
  openThreadsCount,
  threads,
  // Per-lens totals rendered next to each tab label. null/undefined → no count.
  threadLensCounts,
  threadLens = "open",
  onThreadLensChange,
  showQuiet = false,
  onToggleQuiet,
  onLoadMoreThreads,
  selection,
  onSelect,
  channelsStatus = "ready",
  dmsStatus = "ready",
  threadsStatus = "ready",
  onRetryRooms,
  onRetryThreads,
}) {
  const r = rooms || {};
  const channels = Array.isArray(r.channels) ? r.channels : [];
  const dms = Array.isArray(r.dms) ? r.dms : [];
  const counts = r.counts || {};
  const th = threads || {};
  const items = Array.isArray(th.items) ? th.items : [];
  const sel = selection || {};

  return (
    <div
      className="flex h-full w-full flex-col overflow-y-auto"
      data-testid="conversations-rail"
    >
      {/* ── Channels (a place) ───────────────────────────────────────── */}
      <PanelErrorBoundary label="conversations-rail-channels">
        <SectionHeader
          label="Channels"
          count={counts.channels}
          trailing={
            <button
              type="button"
              onClick={onToggleQuiet}
              aria-pressed={showQuiet}
              className="text-[11px] font-medium text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              data-testid="quiet-toggle"
            >
              {showQuiet ? "Hide quiet" : "Show quiet"}
            </button>
          }
        />
        {channelsStatus === "loading" ? (
          <ChannelsRowsSkeleton />
        ) : channelsStatus === "error" ? (
          <div className="px-3 py-2">
            <ErrorState
              variant="inset"
              message="Channels are temporarily unavailable. Please try again shortly."
              onRetry={onRetryRooms}
            />
          </div>
        ) : channels.length === 0 ? (
          <div className="px-3 py-2">
            <EmptyState variant="inset" message="No channels." padding="py-4" />
          </div>
        ) : (
          <ul className="space-y-0.5 px-2">
            {channels.map((ch) => (
              <li key={ch.id}>
                <ChannelRow
                  channel={ch}
                  selected={sel.type === "channel" && sel.id === ch.id}
                  onSelect={(id) => onSelect("channel", id)}
                />
              </li>
            ))}
          </ul>
        )}
      </PanelErrorBoundary>

      {/* ── DMs (a place) ────────────────────────────────────────────── */}
      <PanelErrorBoundary label="conversations-rail-dms">
        <SectionHeader label="DMs" count={counts.dms} />
        {dmsStatus === "loading" ? (
          <DmsRowsSkeleton />
        ) : dmsStatus === "error" ? (
          <div className="px-3 py-2">
            <ErrorState
              variant="inset"
              message="Direct messages are temporarily unavailable. Please try again shortly."
              onRetry={onRetryRooms}
            />
          </div>
        ) : dms.length === 0 ? (
          <div className="px-3 py-2">
            <EmptyState variant="inset" message="No direct messages." padding="py-4" />
          </div>
        ) : (
          <ul className="space-y-0.5 px-2">
            {dms.map((dm) => (
              <li key={dm.id}>
                <DmRow
                  dm={dm}
                  selected={sel.type === "dm" && sel.id === dm.id}
                  onSelect={(id) => onSelect("dm", id)}
                />
              </li>
            ))}
          </ul>
        )}
      </PanelErrorBoundary>

      {/* ── Threads (a queue) ────────────────────────────────────────── */}
      <PanelErrorBoundary label="conversations-rail-threads">
        <SectionHeader label="Threads" count={openThreadsCount ?? counts.open_threads} />
        <div className="flex items-center gap-1 px-3 pb-1" role="tablist" aria-label="Thread lenses">
          {THREAD_LENS.map((lens) => {
            const lensCount = threadLensCounts ? threadLensCounts[lens.key] : undefined;
            const active = threadLens === lens.key;
            return (
              <button
                key={lens.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`thread-lens-${lens.key}`}
                onClick={() => onThreadLensChange(lens.key)}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "bg-[#32B1FF] text-white"
                    : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                }`}
              >
                {lens.label}
                {typeof lensCount === "number" && (
                  <span
                    data-testid={`thread-lens-count-${lens.key}`}
                    className={`tabular-nums text-[10px] ${
                      active ? "text-white/80" : "text-gray-400"
                    }`}
                  >
                    {fmtNumber(lensCount)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {threadsStatus === "loading" ? (
          <ThreadsRowsSkeleton />
        ) : threadsStatus === "error" ? (
          <div className="px-3 py-2">
            <ErrorState
              variant="inset"
              message="Threads are temporarily unavailable. Please try again shortly."
              onRetry={onRetryThreads}
            />
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-2">
            <EmptyState variant="inset" message="No open threads." padding="py-4" />
          </div>
        ) : (
          <>
            <ul className="space-y-1.5 px-2 pt-1">
              {items.map((t) => (
                <li key={t.id}>
                  <ThreadRow
                    thread={t}
                    selected={sel.type === "thread" && sel.id === t.id}
                    onSelect={(id) => onSelect("thread", id)}
                  />
                </li>
              ))}
            </ul>
            {th.next_before && (
              <div className="px-3 py-2">
                <button
                  type="button"
                  onClick={onLoadMoreThreads}
                  className="w-full rounded-lg border border-gray-200 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800"
                  data-testid="threads-load-more"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </PanelErrorBoundary>
    </div>
  );
}

